import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import useRevealOnScroll from '../hooks/useRevealOnScroll';
import './ProPage.css';

// Mirrors CheckoutPage.js STRIPE_LINKS — keep in sync.
const STRIPE_LINKS = {
  monthly:   'https://buy.stripe.com/4gM4gz0M8c85cxK6LE1kA0A',
  quarterly: 'https://buy.stripe.com/cNi4gzamI3Bz1T60ng1kA0B',
  yearly:    'https://buy.stripe.com/7sYaEXbqM1tr0P20ng1kA0C',
};

const featureIcons = [
  // 0: Priority Inbox — chat bubble
  (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>),
  // 1: Neighbourhood Intelligence — globe
  (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>),
  // 2: Smart Alerts — bell
  (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>),
  // 3: Price Drop Alerts — trending down
  (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>),
];

const FEATURE_KEYS = ['priorityInbox', 'neighbourhood', 'smartAlerts', 'priceDropAlerts'];

const PLAN_IDS = ['monthly', 'quarterly', 'yearly'];
const PLAN_PRICES = { monthly: '12.89', quarterly: '30.57', yearly: '114.47' };
const PLAN_PERIODS = { monthly: '/mo', quarterly: '/qtr', yearly: '/yr' };

function RevealFeature({ index, children }) {
  const [ref, visible] = useRevealOnScroll(0.25, { rootMargin: '0px 0px -10% 0px' });
  return (
    <div
      ref={ref}
      className={`proPage__feature featureReveal${visible ? ' featureReveal--visible' : ''}`}
      style={visible ? { animationDelay: `${index * 90}ms` } : undefined}
    >
      {children}
    </div>
  );
}

export default function ProPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [selected, setSelected] = useState('quarterly');

  const goToStripe = () => {
    const url = new URL(STRIPE_LINKS[selected]);
    if (user?.email) url.searchParams.set('prefilled_email', user.email);
    if (user?.id)    url.searchParams.set('client_reference_id', user.id);
    window.location.href = url.toString();
  };

  const plans = PLAN_IDS.map(id => ({
    id,
    label: t(`checkout.plans.${id}.label`),
    price: PLAN_PRICES[id],
    period: PLAN_PERIODS[id],
    note: t(`checkout.plans.${id}.note`),
    discount: id === 'monthly' ? null : t(`checkout.plans.${id}.discount`),
  }));

  const features = FEATURE_KEYS.map(k => {
    const entry = t(`proPage.features.${k}`);
    return typeof entry === 'object' && entry !== null ? entry : { title: k };
  });

  return (
    <div className="proPage">

      <section className="proPage__hero">
        <div className="proPage__heroInner">
          <span className="proPage__eyebrow">
            <span className="proPage__eyebrowLine" />
            <span className="proPage__eyebrowText">{t('home.pro.eyebrow')}</span>
            <span className="proPage__eyebrowLine" />
          </span>
          <h1 className="proPage__title">{t('home.pro.title')}</h1>
          <p className="proPage__subtitle">{t('home.pro.subtitle')}</p>
          <div className="proPage__ctaRow">
            <button className="proPage__ctaPrimary" onClick={goToStripe}>
              {t('home.pro.getStarted')}
            </button>
            <button className="proPage__ctaSecondary" onClick={() => {
              document.getElementById('proPage__plans')?.scrollIntoView({ behavior: 'smooth' });
            }}>
              {t('home.pro.seePlans')}
            </button>
          </div>
        </div>
      </section>

      <section className="proPage__features">
        <div className="proPage__featuresInner">
          <h2 className="proPage__featuresTitle">{t('home.pro.whatsIncluded')}</h2>
          <div className="proPage__featuresList">
            {features.map((f, i) => (
              <RevealFeature key={i} index={i}>
                <div className="proPage__featureIcon">{featureIcons[i]}</div>
                <h3 className="proPage__featureTitle">{f.title}</h3>
                {f.bullets ? (
                  <ul className="proPage__featureBullets">
                    {f.bullets.map((b, j) => <li key={j}>{b}</li>)}
                  </ul>
                ) : (
                  <p className="proPage__featureDesc">{f.desc}</p>
                )}
              </RevealFeature>
            ))}
          </div>
        </div>
      </section>

      <section className="proPage__plansSection" id="proPage__plans">
        <div className="proPage__plansInner">
          <h2 className="proPage__plansTitle">{t('proPage.choosePlan')}</h2>
          <div className="proPage__plans">
            {plans.map(p => (
              <button
                key={p.id}
                type="button"
                className={`proPage__plan${selected === p.id ? ' proPage__plan--selected' : ''}`}
                onClick={() => setSelected(p.id)}
                aria-pressed={selected === p.id}
              >
                <div className="proPage__planLabel">{p.label}</div>
                <div className="proPage__planPrice">
                  <span className="proPage__planCurrency">EUR</span>
                  <span className="proPage__planAmount">{p.price}</span>
                  <span className="proPage__planPeriod">{p.period}</span>
                </div>
                <div className="proPage__planNote">{p.note}</div>
                {p.discount && <div className="proPage__planDiscount">{p.discount}</div>}
              </button>
            ))}
          </div>
          <button className="proPage__planCta" onClick={goToStripe}>
            {t('home.pro.getStartedWith').replace('{label}', plans.find(p => p.id === selected)?.label)}
          </button>
        </div>
      </section>

    </div>
  );
}
